import AlignmentServiceViewport from '../ViewportTypes/AlignmentServiceViewport.js';
import ViewportProviderInterface from './ViewportProviderInterface.js';
import { uniqWith } from 'lodash-es';

export default class AlignmentServiceViewportProvider extends ViewportProviderInterface {
    /** @param options:
    *                   Must include a service (AlignmentService)
    *                   Must include a getVersionUrn function that given a lineageUrn returns the latest versionUrn
    *                   Must include a loadDocument function that fetches the document for a model
    *
    */
    constructor(options) {
        super(options);

        this.service = this.options.service;
        this.getVersionUrn = this.options.getVersionUrn;
        this.loadDocument = this.options.loadDocument;

        if (!this.service || !this.getVersionUrn || !this.loadDocument) {
            console.error('One of AlignmentServiceViewportProvider callbacks is missing.');
            throw 'AlignmentServiceViewportProvider callbacks is missing';
        }

        this.getModel3DInfoRelatedToViewport = this.getModel3DInfoRelatedToViewport.bind(this);
        this.fetch3DNodeForViewport = this.fetch3DNodeForViewport.bind(this);
        this.fetchSheetNodeForViewport = this.fetchSheetNodeForViewport.bind(this);
    }

    /**
     * Fetch viewports for a given bubble.
     *
     * @param {Autodesk.Viewing.BubbleNode} bubbleNode - bubbleNode.
     *
     * @returns {Promise<[Viewport]>} Promise with array of viewports.
     */
    async fetchViewports(bubbleNode) {
        const viewports = await (bubbleNode.is2D() ? this.fetchViewportsFromBubble2D(bubbleNode) : this.fetchViewportsFromBubble3D(bubbleNode));

        return viewports;
    }

    /**
     * @returns {string} Name of viewport provider
     */
    static getProviderName() {
        return 'AlignmentServiceViewportProvider';
    }

    // Fetches viewports for a model and save it in cache, using the provided service
    async fetchViewportsFromBubble2D(bubbleNode) {
        const viewableName2D = bubbleNode.name();

        // Given lineageUrn, get latest versionUrn.
        const lineageUrn2D = bubbleNode.lineageUrn();
        let versionUrn2D;
        try {
            versionUrn2D = await this.getVersionUrn(lineageUrn2D);
        } catch (e) {
            console.log('Could not get latest version. Probably no permission or document has been deleted');
            return [];
        }
        const versionUrn2DEncoded = Autodesk.Viewing.Private.toUrlSafeBase64(versionUrn2D);

        const viewportsRaw = await this.service.loadRegions(versionUrn2DEncoded, viewableName2D);

        if (!viewportsRaw || !viewportsRaw.length) {
            return [];
        }

        const viewports = await Promise.all(viewportsRaw.map(async viewportRaw => {
            // Verify that the regions that we got from Alignment Service actually have linked 3D models attached to them.
            // Usually, when creating an alignment between a 2D sheet and a 3D model, a relationship must be created.
            // However, in case that a document with alignments was copied, the alignment service will copy the regions to the new cloned document,
            // BUT the relationship won't get copied.
            const relationships = await this.service.getRelationships(Autodesk.AlignmentService.RelationshipEntityTypes.Region, lineageUrn2D, viewableName2D, viewportRaw.regionId);
            const supports2DTo3DTransform = relationships.length > 0;

            // Since viewport name is not included in the service - we use the sheet's name for now.
            const viewport = this._createViewport(viewportRaw, viewableName2D, versionUrn2DEncoded, viewableName2D, supports2DTo3DTransform);

            return viewport;
        }));

        return viewports;
    }

    async fetchViewportsFromBubble3D(bubbleNode) {
        const lineageUrn3D = bubbleNode.lineageUrn();
        const viewableName3D = bubbleNode.name();

        let relationships;

        try {
            relationships = await this.service.getRelationships(Autodesk.AlignmentService.RelationshipEntityTypes.Model3D, lineageUrn3D, viewableName3D);
        } catch (e) {
            console.warn('getRelationships Error: ' + e);
            return [];
        }

        if (!relationships.length) {
            return [];
        }

        // Gather viewports for batch download, in parallel
        const regionsParameters = [];
        await Promise.all(relationships.map(async (relationship) => {
            const relatedModel = relationship.entities.find(e => e.type === Autodesk.AlignmentService.RelationshipEntityTypes.Region);

            if (!relatedModel) {
                console.warn('Related model not found');
                return;
            }

            const { lineageUrn, rest } = this._splitId(relatedModel.id);

            const nameAndRegion = rest.slice(Autodesk.AlignmentService.RelationshipIdPrefix.Name.length);
            const [name2D, regionId] = nameAndRegion.split(Autodesk.AlignmentService.RelationshipIdPrefix.Region);

            // Given lineageUrn, get latest versionUrn.
            let versionUrn2D;
            try {
                versionUrn2D = await this.getVersionUrn(lineageUrn);
            } catch (e) {
                console.log('Could not get latest version for alignment relationship. Probably no permission or document has been deleted');
                return;
            }

            const versionUrn2DEncoded = Autodesk.Viewing.Private.toUrlSafeBase64(versionUrn2D);

            regionsParameters.push({ versionUrn: versionUrn2DEncoded, viewablePartId: name2D, regionId, createdOn: new Date(relatedModel.createdOn || 0) });
        }));

        // Only urn and partId are needed to load the regions, and they could be duplicated (several regionIds for the same urn/partId combo)
        const dedupedParameters = uniqWith(regionsParameters, (a, b) => a.versionUrn === b.versionUrn && a.viewablePartId === b.viewablePartId);
        let allRegions = await this.service.loadBatchedRegions(dedupedParameters);
        // The loadBatchedRegions function will return an empty region if it doesn't exist, so remove undefined.
        // Also flatten and get a simple array of regionIds
        allRegions = allRegions.filter(e => e).flat();

        // Retrieval order is not stable, so we sort by creation time from old to new
        regionsParameters.sort((regionA, regionB) => regionA.createdOn - regionB.createdOn);

        const relatedViewports = [];
        regionsParameters.forEach((regionParameter) => {
            const { versionUrn, viewablePartId, regionId } = regionParameter;

            const viewportRaw = allRegions.find(r => r.regionId === regionId);
            if (!viewportRaw) {
                console.warn('Viewport not found.', versionUrn, viewablePartId, regionId);
                return;
            }

            // Since viewport name is not included in the service - we use the sheet's name (viewablePartId) for now.
            const viewport = this._createViewport(viewportRaw, viewablePartId, versionUrn, viewablePartId, true);

            relatedViewports.push(viewport);
        });

        return relatedViewports;
    }

    async fetchSheetNodeForViewport(viewport) {
        const result = await this._fetchBubbleNode(viewport.getSheetUrn(), viewport.getSheetName());
        return result;
    }

    // Returns { versionUrn3D, lineageUrn3D, name3D } for the 3D model related to a viewport
    async getModel3DInfoRelatedToViewport(viewport) {
        const lineageUrn2D = viewport.getSheetLineageUrn();
        const viewableName2D = viewport.getSheetName();
        const regionId = viewport.getGuid();

        let relationship;
        try {
             // Get first relationship (Should be only one since regionId is unique)
            [relationship] = await this.service.getRelationships(Autodesk.AlignmentService.RelationshipEntityTypes.Region, lineageUrn2D, viewableName2D, regionId);
        } catch (e) {
            console.warn('No relationship found. Error: ' + e);
            throw e;
        }

        const relatedModel = relationship?.entities.find(e => e.type === Autodesk.AlignmentService.RelationshipEntityTypes.Model3D);

        if (!relatedModel) {
            throw 'No related model found for sheet-model transition';
        }

        const { lineageUrn: lineageUrn3D, rest: name3D } = this._splitId(relatedModel.id);

        // Given lineageUrn, get latest versionUrn.
        let versionUrn3D;
        try {
            versionUrn3D = await this.getVersionUrn(lineageUrn3D);
        } catch (e) {
            console.log('Could not get latest version for alignment relationship. Probably no permission or document has been deleted');
            throw e;
        }

        return { versionUrn3D, lineageUrn3D, name3D };
    }

    async fetch3DNodeForViewport(viewport) {
        const { versionUrn3D, name3D } = await this.getModel3DInfoRelatedToViewport(viewport);

        // Encode versionUrn
        const urn3DEncoded = Autodesk.Viewing.toUrlSafeBase64(versionUrn3D);

        const name = decodeURIComponent(name3D.slice(Autodesk.AlignmentService.RelationshipIdPrefix.Name.length));

        const node = await this._fetchBubbleNode(urn3DEncoded, name);

        // For every viewport from Alignment Service there is only a single 3D geometry connected to it.
        // However, the generic API expects an array of 3D nodes, so just wrap it with an array.
        return [node];
    }

    async deleteViewport(vp) {
        await this.service.deleteRegion(vp.lineageUrn, vp.sheetUrn, vp.sheetName, vp.viewportRaw.regionId);

        return true;
    }

    _createViewport(viewportRaw, viewportName, urn2D, sheetName, supports2DTo3DTransform) {
        const options = {
            getModel3DInfoRelatedToViewport: this.getModel3DInfoRelatedToViewport,
            fetch3DNodeForViewport: this.fetch3DNodeForViewport,
            fetchSheetNodeForViewport: this.fetchSheetNodeForViewport,
            supports2DTo3DTransform,
        };

        return new AlignmentServiceViewport(viewportRaw, viewportName, urn2D, sheetName, options);
    }

    async _fetchBubbleNode(urn, name) {
        let doc;

        try {
            doc = await this.loadDocument(urn);
        } catch (e) {
            throw 'Could not load document';
        }

        const rootNode = doc.getRoot();
        const nodes = rootNode.search({ type: 'geometry', name });

        return nodes[0];
    }

    _splitId(id) {
        // Find the first '?' only, in case the name contains one inside
        const index = id.indexOf('?');
        if (index < 0) {
            throw 'Malformed relationship id';
        }

        const lineageUrn = id.substring(0, index);
        const rest = id.substring(index + 1);

        return { lineageUrn, rest };
    }
}
